import React, {useState} from 'react'
import {Translate} from "../../../components/translate/Translate";
import axios from "axios";
import {getApiLink} from "../../../functions/getApiLink";
import {NavLink, useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {useDispatch} from "react-redux";
import {setAccessToken, setRefreshToken, setUser} from "../../../storage/toolkit";
import setCookie from "../../../functions/setCookie";
import {access_token, refresh_token} from "../../../api/CookieKeys";
import {getBearer} from "../../../functions/getBearer";
import {passwordErrorKey, passwordProblem} from "../../../functions/passwordPolicy";

interface IResetPasswordFormProps {

}

/**
 * One form, two ways in.
 *
 * From the "forgot password" letter it arrives with a code and an e-mail in
 * the URL and posts to the public reset endpoint. Reached as /change-password
 * the visitor is already signed in - a temporary password got them that far -
 * so there is no code to carry and the authenticated endpoint is used, which
 * answers with a fresh token pair because saving the password revokes the one
 * they are holding.
 */
export const ResetPasswordForm: React.FC<IResetPasswordFormProps> = () => {

    const {userCode, userEmail} = useParams()
    const isSessionMode = !userCode || !userEmail

    const [isSuccessChanges, setIsSuccessChanges] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string>("")
    const [isShowPassword, setIsShowPassword] = useState(false)
    const [isShowPasswordRepeat, setIsShowPasswordRepeat] = useState(false)
    const [passwordField, setPasswordField] = useState("")
    const [secondPasswordField, setSecondPasswordField] = useState("")
    const [isSaving, setIsSaving] = useState(false)

    const {t} = useTranslation();
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const reportServerError = (code?: string) => {
        const key = passwordErrorKey(code)
        if (key) return toast.error(`${t(key)}`)
        setErrorMessage(code || "unknown")
    }

    const handleReset = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setErrorMessage("")

        const problem = passwordProblem(passwordField, secondPasswordField)
        if (problem) return toast.error(`${t(problem)}`)

        setIsSaving(true)

        if (isSessionMode) {
            getBearer('post')
            axios.post(getApiLink("/api/user/change_password/"), {
                "password": passwordField,
                "password_repeat": secondPasswordField
            }).then(({data}) => {
                setIsSaving(false)
                if (data?.status === false) return reportServerError(data.message)

                // Saving the password revoked the token that carried this
                // request, so swap in the pair the API handed back before
                // anything else fires.
                setCookie(access_token, data.access_token)
                setCookie(refresh_token, data.refresh_token)
                dispatch(setUser(data.user))
                dispatch(setAccessToken(data.access_token))
                dispatch(setRefreshToken(data.refresh_token))
                navigate("/")
            }).catch(er => {
                console.log(er)
                setIsSaving(false)
                setErrorMessage("unknown")
            })
            return
        }

        axios.post(getApiLink("/api/auth/reset_password_confirm/"), {
            "email": userEmail,
            "code": userCode,
            "password": passwordField
        }).then(({data}) => {
            setIsSaving(false)
            if (data.status === true) {
                setIsSuccessChanges(true)
            } else if (data.status === false) {
                reportServerError(data.message)
            }
        }).catch(er => {
            console.log(er)
            setIsSaving(false)
            setErrorMessage("unknown")
        })
    }

    const errorCodes: any = {
        "invalid_code": "Invalid code",
        "user_not_found": `${t("user_not_found")}`,
        "unknown": `${t("errors.500")}`,
    }

    return (
        <form onSubmit={handleReset} className="login__form">

            {isSuccessChanges ? <div className="reset-password__thanks">
                    <h1 className="login__title title is-large">
                        <Translate>reset_password.thank_you</Translate>
                    </h1>
                    <p>
                        <Translate>reset_password.password_successfully_changed</Translate>
                    </p>
                    <NavLink to={"/login"} className={"login__submit btn"}>
                        <Translate>reset_password.go_to_the_entry_page</Translate>
                    </NavLink>
                </div> :
                <>
                    <h1 className="login__title title is-large">
                        <Translate>
                            {isSessionMode ? "change_password.title" : "reset_password.reset_password"}
                        </Translate>
                    </h1>
                    {isSessionMode && <p className="login__subtitle">
                        <Translate>change_password.subtitle</Translate>
                    </p>}
                    <label className="login__label">
                        <input type={isShowPassword ? "text" : "password"} name="password" required
                               onChange={e => setPasswordField(e.target.value.trim())} value={passwordField}
                               className="login__input input password-input"
                               placeholder={`${t("reset_password.enter_new_password")}`}
                        />
                        <button onClick={_ => setIsShowPassword(prev => !prev)}
                                className="login__show-password password-input__visible-toggle" type="button"
                                title="Show/Hide password">
                            <svg width="15" height="15" viewBox="0 0 15 15">
                                <use xlinkHref={isShowPassword ? "#password-viewed" : "#password-hidden"}/>
                            </svg>
                        </button>
                    </label>
                    <label className="login__label">
                        <input type={isShowPasswordRepeat ? "text" : "password"} name="password" required
                               onChange={e => setSecondPasswordField(e.target.value.trim())}
                               value={secondPasswordField}
                               className="login__input input password-input"
                               placeholder={`${t("reset_password.confirm_new_password")}`}
                        />
                        <button onClick={_ => setIsShowPasswordRepeat(prev => !prev)}
                                className="login__show-password password-input__visible-toggle" type="button"
                                title="Show/Hide password">
                            <svg width="15" height="15" viewBox="0 0 15 15">
                                <use xlinkHref={isShowPasswordRepeat ? "#password-viewed" : "#password-hidden"}/>
                            </svg>
                        </button>
                    </label>

                    <div className="login__password-rules">
                        <span><Translate>password_rules.title</Translate></span>
                        <ul>
                            <li><Translate>password_rules.min_length</Translate></li>
                            <li><Translate>password_rules.uppercase</Translate></li>
                            <li><Translate>password_rules.lowercase</Translate></li>
                            <li><Translate>password_rules.digit</Translate></li>
                            <li><Translate>password_rules.special</Translate></li>
                        </ul>
                    </div>

                    <div className="login__form-row">
                        <div></div>
                        <button disabled={isSaving} className="login__submit btn" type="submit">
                            <Translate>
                                {isSessionMode ? "change_password.save" : "reset_password.save_new_password"}
                            </Translate>
                        </button>
                    </div>
                    <p className="error">
                        {errorCodes[errorMessage]}
                    </p>

                </>

            }


        </form>
    )
}
